import React, {useEffect, useState, useRef} from 'react';
import {classes} from './CodeInputSection.st.css';
import {TextButton, TextButtonPriority} from '@wix/wix-ui-tpa/cssVars';
import {CodeInput} from './CodeInput/CodeInput';
import {Tooltip} from '@wix/wix-ui-tpa';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../common/constants';

export interface CodeInputSectionProps {
  dataHook: string;
  enterACodeButtonText: string;
  ariaLabel?: string;
  onCodeInputOpen?: () => void;
  applyCodeText: string;
  removeCodeText: string;
  codeValue?: string;
  isLoading: boolean;
  onApplyCode: (code: string) => void;
  onRemoveCode: () => void;
  error?: string;
  icon?: React.ReactElement;
  placeholder?: string;
  isUpdating?: boolean;
  isEnterACodeButtonDisabled: boolean;
  enterACodeButtonDisabledTooltipData: {dataHook: string; content: string};
  children?: React.ReactNode;
}

export enum CodeInputSectionDataHooks {
  EnterACodeButton = 'CodeInputSection.EnterACodeButton',
  InputSection = 'CodeInputSection.InputSection',
}

export const CodeInputSection = ({
  dataHook,
  enterACodeButtonText,
  onCodeInputOpen,
  applyCodeText,
  removeCodeText,
  codeValue,
  isLoading,
  onApplyCode,
  onRemoveCode,
  error,
  icon,
  placeholder,
  isUpdating,
  isEnterACodeButtonDisabled,
  enterACodeButtonDisabledTooltipData,
  ariaLabel,
  children,
}: CodeInputSectionProps) => {
  const [isCollapsed, setIsCollapsed] = useState(!codeValue);
  const {experiments} = useExperiments();
  const couponAlignmentInCartAndCheckout = experiments.enabled(SPECS.CouponAlignmentInCartAndCheckout);
  const ref = useRef<HTMLInputElement | null>(null);
  const isActionInProgress = useRef(false);

  useEffect(() => {
    if (!codeValue) {
      return;
    }

    setIsCollapsed(!codeValue);
  }, [codeValue]);

  useEffect(() => {
    if (isCollapsed) {
      return;
    }
    if (!codeValue && isActionInProgress.current) {
      ref.current?.focus();
      isActionInProgress.current = false;
    }
  }, [isCollapsed, codeValue]);

  const textButton = (
    <TextButton
      className={classes.textButton}
      data-hook={CodeInputSectionDataHooks.EnterACodeButton}
      priority={TextButtonPriority.primary}
      prefixIcon={icon}
      aria-expanded={!isCollapsed}
      onClick={() => {
        if (isCollapsed) {
          isActionInProgress.current = true;
          onCodeInputOpen?.();
        }
        setIsCollapsed(!isCollapsed);
      }}
      disabled={isEnterACodeButtonDisabled}>
      {enterACodeButtonText}
    </TextButton>
  );

  return (
    <div data-hook={dataHook}>
      <div className={classes.enterACodeButton}>
        {isEnterACodeButtonDisabled ? (
          <Tooltip
            content={enterACodeButtonDisabledTooltipData.content}
            data-hook={enterACodeButtonDisabledTooltipData.dataHook}>
            {textButton}
          </Tooltip>
        ) : (
          textButton
        )}
      </div>
      {!isCollapsed && (
        <div className={couponAlignmentInCartAndCheckout ? classes.codeInput : classes.codeInputOld}>
          <CodeInput
            dataHook={CodeInputSectionDataHooks.InputSection}
            applyText={applyCodeText}
            removeText={removeCodeText}
            appliedValue={codeValue}
            isLoading={isLoading}
            onApply={onApplyCode}
            onRemove={() => {
              isActionInProgress.current = true;
              onRemoveCode();
            }}
            error={error}
            placeholder={placeholder}
            isUpdating={isUpdating}
            ariaLabel={ariaLabel}
            ref={ref}
          />
          {children}
        </div>
      )}
    </div>
  );
};
